import { useState, useEffect, useRef, useCallback } from 'react';

const DEFAULT_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const DEFAULT_WARNING_MS = 30 * 1000; // 30 seconds warning before timeout

interface InactivityTimerOptions {
  timeout?: number; // Total inactivity timeout in ms
  warningTime?: number; // Warning time in ms before timeout
  onTimeout?: () => void;
  onExtend?: () => void;
  enabled?: boolean;
}

interface InactivityTimerState {
  remaining: number; // Remaining ms before timeout
  isWarning: boolean; // Show warning modal
  isExpired: boolean; // Timeout reached
  reset: () => void; // Reset the timer
  extend: () => void; // Extend session (reset + refresh)
}

export function useInactivityTimer(options: InactivityTimerOptions = {}): InactivityTimerState {
  const {
    timeout = DEFAULT_TIMEOUT_MS,
    warningTime = DEFAULT_WARNING_MS,
    onTimeout,
    onExtend,
    enabled = true,
  } = options;

  const [remaining, setRemaining] = useState(timeout);
  const [isWarning, setIsWarning] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutCalledRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    setRemaining(timeout);
    setIsWarning(false);
    setIsExpired(false);
    timeoutCalledRef.current = false;
  }, [timeout]);

  const extend = useCallback(() => {
    reset();
    onExtend?.();
  }, [reset, onExtend]);

  // Main countdown interval
  useEffect(() => {
    if (!enabled) {
      clearTimer();
      return;
    }

    intervalRef.current = setInterval(() => {
      setRemaining((prev) => {
        const newVal = prev - 1000;

        // Enter warning state
        if (newVal <= warningTime && !timeoutCalledRef.current) {
          setIsWarning(true);
        }

        // Timeout reached
        if (newVal <= 0 && !timeoutCalledRef.current) {
          timeoutCalledRef.current = true;
          setIsWarning(false);
          setIsExpired(true);
          clearTimer();
          onTimeout?.();
          return 0;
        }

        return Math.max(newVal, 0);
      });
    }, 1000);

    return () => {
      clearTimer();
    };
  }, [enabled, warningTime, clearTimer, onTimeout]);

  // Activity event listeners
  useEffect(() => {
    if (!enabled) {return;}

    const activityEvents = ['mousedown', 'keydown', 'mousemove', 'click', 'scroll', 'touchstart'];

    const handleActivity = () => {
      // Only reset if not already expired
      if (!timeoutCalledRef.current) {
        setRemaining(timeout);
        setIsWarning(false);
      }
    };

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [enabled, timeout]);

  return {
    remaining,
    isWarning,
    isExpired,
    reset,
    extend,
  };
}
